import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { GoogleAuthDebug } from '../components/Auth/GoogleAuthDebug';
import { GoogleSignInButton } from '../components/Auth/GoogleSignInButton';
import { useAuth } from '../contexts/AuthContext';
/**
 * Google Auth Test Page
 * Use this page to test Google OAuth integration
 * Add to AppRouter.tsx: <Route path="/test-google" element={<GoogleAuthTestPage />} />
 */
export function GoogleAuthTestPage() {
  const {
    user,
    signInWithGoogle,
    signOut
  } = useAuth();
  const [loading, setLoading] = useState(false);
  const handleGoogleSignIn = async () => {
    setLoading(true);
    try {
      await signInWithGoogle();
    } catch (error) {
      console.error('Sign-in error:', error);
      setLoading(false);
    }
  };
  const handleSignOut = async () => {
    await signOut();
  };
  return <div className="flex flex-col w-full min-h-screen bg-[#f8f6f2]" data-id="element-3876" data-mp="owkhs57tuh05">
      <header className="bg-white border-b border-gray-200" data-id="element-3877" data-mp="oyf33kn0gb4r">
        <div className="max-w-4xl mx-auto px-5 py-4" data-id="element-3878" data-mp="3i9wmm87eo79w">
          <Link to="/" className="inline-flex items-center text-gray-600 hover:text-purple-600 text-sm" data-id="element-3879" data-mp="3n7y3cmhczui7">
            <ArrowLeft size={16} className="mr-1" data-id="element-3880" data-mp="389nmijaam1p8" />
            Back to Home
          </Link>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center px-5 py-12" data-id="element-3881" data-mp="3d8798lwpnhin">
        <div className="w-full max-w-2xl" data-id="element-3882" data-mp="2zhc7ruojk2p3">
          <div className="bg-white rounded-xl shadow-sm p-8 mb-6" data-id="element-3883" data-mp="2zgamyyhq41u6">
            <h1 className="text-3xl font-bold mb-2" data-id="element-3884" data-mp="349vkyqjbgvtd">
              🧪 Google OAuth Test Page
            </h1>
            <p className="text-gray-600 mb-6" data-id="element-3885" data-mp="2plqx785epu3q">
              Use this page to test your Google Sign-In integration
            </p>

            {/* Current Status */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6" data-id="element-3886" data-mp="2gj9dp1h4zfkf">
              <h2 className="font-semibold mb-3" data-id="element-3887" data-mp="2glvmf4frk148">Current Status</h2>
              <div className="space-y-2 text-sm" data-id="element-3888" data-mp="2kkl2x5w3lys3">
                <div className="flex items-center justify-between" data-id="element-3889" data-mp="r2vycs9amocr">
                  <span data-id="element-3890" data-mp="r471qqyttu3c">Authentication:</span>
                  <span className={`font-medium ${user ? 'text-green-600' : 'text-red-600'}`} data-id="element-3891" data-mp="mbdu9vbz47nk">
                    {user ? '✅ Logged In' : '❌ Not Logged In'}
                  </span>
                </div>
                {user && <>
                    <div className="flex items-center justify-between" data-id="element-3892" data-mp="1a1hzu88q2hh7">
                      <span data-id="element-3893" data-mp="1525clmdxv8e3">Email:</span>
                      <span className="font-medium" data-id="element-3894" data-mp="1536nnquyfuvt">{user.email}</span>
                    </div>
                    <div className="flex items-center justify-between" data-id="element-3895" data-mp="1020yg30i6pr1">
                      <span data-id="element-3896" data-mp="w206dfkheg1i">Name:</span>
                      <span className="font-medium" data-id="element-3897" data-mp="w0xh1c2t4lnp">
                        {user.user_metadata?.fullName || 'Not set'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between" data-id="element-3898" data-mp="3grg28oyk4uy0">
                      <span data-id="element-3899" data-mp="1w9x6hjwi9wbi">Provider:</span>
                      <span className="font-medium" data-id="element-3900" data-mp="1waygdl1sccrn">
                        {user.app_metadata?.provider || 'Unknown'}
                      </span>
                    </div>
                  </>}
              </div>
            </div>

            {/* Test Actions */}
            <div className="space-y-4" data-id="element-3901" data-mp="2a1bte1tjahnu">
              {!user ? <>
                  <h2 className="font-semibold mb-3" data-id="element-3902" data-mp="2f0of2j8o5iou">Test Google Sign-In</h2>
                  <GoogleSignInButton onClick={handleGoogleSignIn} loading={loading} text="Test Google Sign-In" data-id="element-3903" data-mp="2jz7yoczsiie1" />
                  <p className="text-xs text-gray-500 text-center" data-id="element-3904" data-mp="2nz080rx0cskg">
                    Click the button above to test the Google OAuth flow
                  </p>
                </> : <>
                  <h2 className="font-semibold mb-3" data-id="element-3905" data-mp="yldal68k0h9f">
                    Logged In Successfully!
                  </h2>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4" data-id="element-3906" data-mp="3aozcdfgyn3b">
                    <p className="text-sm text-green-800" data-id="element-3907" data-mp="3uit3xfzspryf">
                      ✅ Google Sign-In is working correctly!
                    </p>
                  </div>
                  <button onClick={handleSignOut} className="w-full bg-red-600 text-white py-3 rounded-lg font-medium hover:bg-red-700 transition-colors" data-id="element-3908" data-mp="8atdvxwth7qw">
                    Sign Out
                  </button>
                </>}
            </div>

            {/* Setup Checklist */}
            <div className="mt-8 pt-6 border-t border-gray-200" data-id="element-3909" data-mp="3qhzbc9beroz7">
              <h2 className="font-semibold mb-3" data-id="element-3910" data-mp="3lpfzbgsdd1wi">Setup Checklist</h2>
              <div className="space-y-2 text-sm" data-id="element-3911" data-mp="2y056qml6adtg">
                <label className="flex items-start" data-id="element-3912" data-mp="2xyu3cnvn382v">
                  <input type="checkbox" className="mt-1 mr-2" data-id="element-3913" data-mp="2t123n7or6ho4" />
                  <span data-id="element-3914" data-mp="2t0sajb8evue7">Enabled Google provider in Supabase Dashboard</span>
                </label>
                <label className="flex items-start" data-id="element-3915" data-mp="18hotz89ukr71">
                  <input type="checkbox" className="mt-1 mr-2" data-id="element-3916" data-mp="1da823pprmu4g" />
                  <span data-id="element-3917" data-mp="1dbjay3y2is2f">Added OAuth Client ID and Secret in Supabase</span>
                </label>
                <label className="flex items-start" data-id="element-3918" data-mp="yiza59ye4wo5">
                  <input type="checkbox" className="mt-1 mr-2" data-id="element-3919" data-mp="13kya09ux9bgc" />
                  <span data-id="element-3920" data-mp="1r9r21v8faz5o">Verified redirect URL in Google Cloud Console</span>
                </label>
                <label className="flex items-start" data-id="element-3921" data-mp="1w8c13rbxvcsj">
                  <input type="checkbox" className="mt-1 mr-2" data-id="element-3922" data-mp="1w7svna3h6gve" />
                  <span data-id="element-3923" data-mp="1i9kqiewac995">Disabled "Skip nonce checks" in Supabase</span>
                </label>
                <label className="flex items-start" data-id="element-3924" data-mp="1m9mvawxbhisn">
                  <input type="checkbox" className="mt-1 mr-2" data-id="element-3925" data-mp="1mchekl4lgxkh" />
                  <span data-id="element-3926" data-mp="6susvk5xgpr5">Disabled "Allow users without email" in Supabase</span>
                </label>
              </div>
            </div>

            {/* Quick Links */}
            <div className="mt-6 pt-6 border-t border-gray-200" data-id="element-3927" data-mp="2difxir5imf43">
              <h2 className="font-semibold mb-3" data-id="element-3928" data-mp="2djr2uvelzuxb">Quick Links</h2>
              <div className="space-y-2" data-id="element-3929" data-mp="28k4o2hj4u6me">
                <a href="https://supabase.com/dashboard/project/neadfoxscgjeekvabnoz/auth/providers" target="_blank" rel="noopener noreferrer" className="block text-sm text-blue-600 hover:underline" data-id="element-3930" data-mp="23s3gva0tn638">
                  → Supabase Auth Providers Dashboard
                </a>
                <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" className="block text-sm text-blue-600 hover:underline" data-id="element-3931" data-mp="1pqr2v4581ejz">
                  → Google Cloud Console Credentials
                </a>
                <Link to="/signup" className="block text-sm text-purple-600 hover:underline" data-id="element-3932" data-mp="3a85qzt25sqvr">
                  → Go to Signup Page
                </Link>
                <Link to="/login" className="block text-sm text-purple-600 hover:underline" data-id="element-3933" data-mp="3mqj8e9lush9k">
                  → Go to Login Page
                </Link>
              </div>
            </div>
          </div>

          {/* Instructions */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6" data-id="element-3934" data-mp="2zqbnepfdxbly">
            <h3 className="font-semibold text-blue-900 mb-3" data-id="element-3935" data-mp="2zsen616caj3v">
              📋 Testing Instructions
            </h3>
            <ol className="space-y-2 text-sm text-blue-800" data-id="element-3936" data-mp="33tqd67t4l6cl">
              <li data-id="element-3937" data-mp="38pf79vmdlv1i">1. Complete the setup checklist above</li>
              <li data-id="element-3938" data-mp="3dqmf1uoiqu86">2. Click "Test Google Sign-In" button</li>
              <li data-id="element-3939" data-mp="3do09g0l6uosl">3. You'll be redirected to Google OAuth</li>
              <li data-id="element-3940" data-mp="nzpvjrgyypd1">4. Authorize the app</li>
              <li data-id="element-3941" data-mp="o359us2tjude">5. You'll be redirected back (to /onboarding)</li>
              <li data-id="element-3942" data-mp="syau7mgkp0zd">6. Check that you're logged in</li>
            </ol>
          </div>
        </div>
      </div>

      {/* Debug Panel */}
      <GoogleAuthDebug data-id="element-3943" data-mp="1e1jyphjsecz4" />
    </div>;
}